import { Box, Button, IconButton, Tooltip } from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import DialpadIcon from "@mui/icons-material/Dialpad";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import SettingsIcon from "@mui/icons-material/Settings";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import type { JSX, MouseEvent as ReactMouseEvent } from "react";

/**
 * The in-call control bar (mute, camera, share-screen, add, keypad, settings,
 * hang up), extracted from `CallWindow.tsx`. The camera + share buttons only
 * render when the parent says they're available. Pure presentational.
 *
 * RESPONSIVE by design — the same controls must never clip or wrap badly across
 * the narrow floating window (~340px) and the full-bleed maximized view:
 *   • Every control is a fixed-size round icon button (40px floating / 48px
 *     maximized), `flexShrink: 0` so none get squeezed.
 *   • Floating: one centered row that is allowed to WRAP (`flexWrap`) — if the
 *     window is ever narrower than the button count, controls flow onto a second
 *     row instead of overflowing/clipping. Hang-up is a compact round red icon
 *     button here (a labelled pill does not fit 340px — it was wrapping its text
 *     and clipping off the edge).
 *   • Maximized: three zones — settings far left, hang-up far right (a roomy
 *     labelled pill, since there's space), everything else centered.
 * The screen-share icon is a desktop monitor (`DesktopWindows`) to match the C1
 * webcast client's share control.
 */
const round = {
  bgcolor: "rgba(255,255,255,0.15)",
  color: "common.white",
  "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
} as const;

export function ControlFooter({
  muted,
  onToggleMute,
  showCameraButton,
  cameraOn,
  onToggleCamera,
  showShareButton,
  isSharing,
  onShareScreen,
  showAddButton,
  onOpenAddPeople,
  showKeypadButton,
  onOpenKeypad,
  onOpenSettings,
  onHangup,
  maximized = false,
}: {
  muted: boolean;
  onToggleMute: () => void;
  showCameraButton: boolean;
  cameraOn: boolean;
  onToggleCamera: () => void;
  showShareButton: boolean;
  isSharing: boolean;
  onShareScreen: () => void;
  showAddButton: boolean;
  onOpenAddPeople: (e: ReactMouseEvent<HTMLElement>) => void;
  showKeypadButton: boolean;
  onOpenKeypad: (e: ReactMouseEvent<HTMLElement>) => void;
  onOpenSettings: (e: ReactMouseEvent<HTMLElement>) => void;
  onHangup: () => void;
  maximized?: boolean;
}): JSX.Element {
  // One consistent button footprint, bigger when there's room (maximized).
  const dim = maximized ? 48 : 40;
  const iconBtn = { ...round, width: dim, height: dim, flexShrink: 0 } as const;

  const settingsBtn = (
    <Tooltip title="Audio & video settings">
      <IconButton data-testid="call-settings" onClick={onOpenSettings} aria-label="Audio and video settings" sx={iconBtn}>
        <SettingsIcon />
      </IconButton>
    </Tooltip>
  );

  // Hang up: a roomy labelled pill when maximized; a compact round red icon
  // button when floating (a text pill won't fit the 340px window — it wraps and
  // clips). aria-label stays "Hang up" in both so the control is found the same.
  const hangupBtn = maximized ? (
    <Button
      color="error"
      variant="contained"
      startIcon={<CallEndIcon />}
      onClick={onHangup}
      aria-label="Hang up"
      data-testid="call-hangup"
      sx={{ borderRadius: 6, px: 3, flexShrink: 0, whiteSpace: "nowrap" }}
    >
      Hang up
    </Button>
  ) : (
    <Tooltip title="Hang up">
      <IconButton
        data-testid="call-hangup"
        onClick={onHangup}
        aria-label="Hang up"
        sx={{ width: dim, height: dim, flexShrink: 0, bgcolor: "error.main", color: "common.white", "&:hover": { bgcolor: "error.dark" } }}
      >
        <CallEndIcon />
      </IconButton>
    </Tooltip>
  );

  // The middle cluster — the same controls in both layouts.
  const middle = (
    <>
      <Tooltip title={muted ? "Unmute" : "Mute"}>
        <IconButton
          data-testid="call-mic"
          onClick={onToggleMute}
          aria-label={muted ? "Unmute microphone" : "Mute microphone"}
          sx={{
            ...iconBtn,
            bgcolor: muted ? "error.main" : round.bgcolor,
            "&:hover": { bgcolor: muted ? "error.dark" : "rgba(255,255,255,0.25)" },
          }}
        >
          {muted ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
      </Tooltip>

      {showCameraButton && (
        <IconButton
          onClick={onToggleCamera}
          aria-label={cameraOn ? "Turn off camera" : "Turn on camera"}
          sx={{
            ...iconBtn,
            bgcolor: cameraOn ? "primary.main" : round.bgcolor,
            "&:hover": { bgcolor: cameraOn ? "primary.dark" : "rgba(255,255,255,0.25)" },
          }}
        >
          {cameraOn ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>
      )}

      {showShareButton && (
        <Tooltip title={isSharing ? "Stop sharing" : "Share screen"}>
          <IconButton
            onClick={onShareScreen}
            aria-label={isSharing ? "Stop sharing" : "Share screen"}
            sx={{
              ...iconBtn,
              bgcolor: isSharing ? "warning.main" : round.bgcolor,
              "&:hover": { bgcolor: isSharing ? "warning.dark" : "rgba(255,255,255,0.25)" },
            }}
          >
            <DesktopWindowsIcon />
          </IconButton>
        </Tooltip>
      )}

      {showAddButton && (
        <Tooltip title="Add someone to the call">
          <IconButton data-testid="call-add-person" onClick={onOpenAddPeople} aria-label="Add someone to the call" sx={iconBtn}>
            <PersonAddIcon />
          </IconButton>
        </Tooltip>
      )}

      {showKeypadButton && (
        <Tooltip title="Dial a phone number">
          <IconButton data-testid="call-keypad" onClick={onOpenKeypad} aria-label="Dial a phone number" sx={iconBtn}>
            <DialpadIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const base = { flexShrink: 0, display: "flex", alignItems: "center", py: 1.25, bgcolor: "rgba(255,255,255,0.04)" } as const;

  if (maximized) {
    // Settings far left, hang-up far right, the rest centered (and the centre
    // cluster may wrap on a narrow maximized width — e.g. a small laptop).
    return (
      <Box sx={{ ...base, justifyContent: "space-between", gap: 1, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>{settingsBtn}</Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 1, rowGap: 1 }}>
          {middle}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flex: 1 }}>{hangupBtn}</Box>
      </Box>
    );
  }

  // Floating: one compact centered row that WRAPS rather than clips/overflows.
  return (
    <Box sx={{ ...base, justifyContent: "center", flexWrap: "wrap", gap: 0.75, rowGap: 0.75, px: 1 }}>
      {middle}
      {settingsBtn}
      {hangupBtn}
    </Box>
  );
}
